import { colors, HumanizedDateCell, Stack, Tooltip, Typography } from '@ui-components/neo.tax';
import { Dayjs } from 'dayjs';
import { ReactNode } from 'react';
import { CellProps, Column } from 'react-table';

import { TaxDocument, DocumentStatus, DocumentStatusBadge } from '@neo.tax/shared';

import { useDownloadTaxDocument } from './hooks';
import { toTaxDocumentName } from './utils';

export const useTaxFormColumns = (): Array<Column<TaxDocument>> => {
  const { downloadTaxDocument } = useDownloadTaxDocument();
  return [
    {
      id: 'name',
      Header: 'Document Name',
      width: 280,
      accessor: (taxDocument): TaxDocument => taxDocument,
      Cell: ({ value }: React.PropsWithChildren<CellProps<TaxDocument, TaxDocument>>): JSX.Element => (
        <Typography fontSize="sm" fontWeight="medium" color={colors.gray[900]}>
          {toTaxDocumentName(value)}
        </Typography>
      ),
    },
    {
      id: 'date',
      Header: 'Generated Date',
      accessor: (taxDocument): string | null => taxDocument.generatedDate || null,
      Cell: ({ value }: React.PropsWithChildren<CellProps<TaxDocument, Dayjs | undefined>>): JSX.Element =>
        value ? (
          <HumanizedDateCell date={value} />
        ) : (
          <Stack justifyContent="end" fullWidth>
            <Typography fontSize="sm" color={colors.gray[500]} textAlign="right">
              -- NA --
            </Typography>
          </Stack>
        ),
    },
    {
      id: 'status',
      Header: 'Status',
      width: 280,
      accessor: (taxDocument): TaxDocument => taxDocument,
      Cell: ({ value: taxDocument }: CellProps<TaxDocument, TaxDocument>): ReactNode => (
        <Tooltip
          key="status"
          className="!z-modal"
          text={taxDocument.missingInformation?.map((reason, index) => <p key={index}>• {reason}</p>) ?? undefined}
          options={{
            placement: 'bottom',
            showArrow: false,
            gutter: 8,
            timeout: 300,
          }}
        >
          {taxDocument.status ? <DocumentStatusBadge status={taxDocument.status} /> : ''}
        </Tooltip>
      ),
    },
    {
      id: 'download',
      Header: '',
      width: 'auto',
      accessor: (taxDocument): TaxDocument => taxDocument,
      Cell: ({ value }: CellProps<TaxDocument, TaxDocument>): ReactNode => (
        <Stack justifyContent="end" fullWidth>
          <Typography
            fontSize="sm"
            disabled={value.status !== DocumentStatus.ReadyForDownload}
            textAlign="right"
            as="button"
            onClick={(): Promise<void> => downloadTaxDocument(value)}
            color={value.status !== DocumentStatus.ReadyForDownload ? colors.gray[300] : colors.primary[600]}
          >
            Download
          </Typography>
        </Stack>
      ),
    },
  ];
};
